import type { MotionStyle } from './styles';

/**
 * Openers: motion styles that go beyond moving the existing layers. A title card built from the
 * headline, cover transitions, camera moves, bursts, confetti and backdrops are generated around
 * the design (see fx.ts and camera.ts), then the layers enter with the choreography of a base
 * style. Times are ms from the start of the video; `intro` is how long the design stays hidden.
 */
export const OPENERS: MotionStyle[] = [
  {
    id: 'opener-panels',
    category: 'opener',
    base: 'energetic',
    label: [
      'Panels opener',
      'Опенер «Панелі»',
      'Otwarcie „Panele”',
      'Deschidere „Panouri”',
      'Apertura «Paneles»',
      'Опенер «Панели»',
    ],
    description: [
      'Headline slams in word by word, colour panels sweep it away',
      'Заголовок вбивається по словах, кольорові панелі змітають його',
      'Nagłówek wpada słowo po słowie, panele zmiatają go',
      'Titlul intră cuvânt cu cuvânt, panourile îl mătură',
      'El título entra palabra a palabra y los paneles lo barren',
      'Заголовок вбивается по словам, цветные панели сметают его',
    ],
    duration: 6000,
    stagger: 110,
    intro: 1600,
    fx: [
      { kind: 'title', start: 0, duration: 1600, params: { style: 'slam', exit: 'cut' } },
      { kind: 'panels', start: 1050, duration: 1100, params: { direction: 'right' } },
    ],
    camera: [{ preset: 'zoom-burst', start: 1600, duration: 800, params: { intensity: 0.7 } }],
  },
  {
    id: 'opener-impact',
    category: 'opener',
    base: 'sale',
    label: [
      'Impact opener',
      'Опенер «Удар»',
      'Otwarcie „Uderzenie”',
      'Deschidere „Impact”',
      'Apertura «Impacto»',
      'Опенер «Удар»',
    ],
    description: [
      'Flash, a slammed title, an iris out into the design with rings and a camera shake',
      'Спалах, титр ударом, діафрагма в дизайн із кільцями і струсом камери',
      'Błysk, tytuł z uderzenia, przesłona z pierścieniami i wstrząsem',
      'Bliț, titlu cu impact, diafragmă cu inele și scuturare',
      'Destello, título de golpe, iris con anillos y sacudida',
      'Вспышка, титр ударом, диафрагма в дизайн с кольцами и тряской камеры',
    ],
    duration: 5500,
    stagger: 100,
    intro: 1400,
    fx: [
      { kind: 'flash', start: 0, duration: 400 },
      {
        kind: 'title',
        start: 0,
        duration: 1500,
        params: { style: 'slam', exit: 'iris', bg: 'accent', accentColor: 'dark' },
      },
      { kind: 'rings', start: 1250, duration: 1300, params: { count: 3 } },
      { kind: 'burst', start: 1250, duration: 800, params: { count: 18, size: 1.4 } },
      {
        kind: 'burst',
        after: 'price',
        offset: -250,
        duration: 700,
        anchor: 'price',
        params: { count: 12, size: 0.6 },
      },
    ],
    camera: [
      { preset: 'shake', start: 1350, duration: 600 },
      { preset: 'punch', after: 'price', offset: -200, duration: 450 },
    ],
  },
  {
    id: 'opener-cinema',
    category: 'opener',
    base: 'cinematic',
    label: [
      'Cinema opener',
      'Опенер «Кіно»',
      'Otwarcie „Kino”',
      'Deschidere „Cinema”',
      'Apertura «Cine»',
      'Опенер «Кино»',
    ],
    description: [
      'Letterbox, a tracked-out title, a slow push-in and a light sweep',
      'Кінорамка, титр з розрядкою, повільний наїзд і відблиск',
      'Kadr kinowy, rozstrzelony tytuł, powolny najazd i odblask',
      'Bandă cinema, titlu spațiat, apropiere lentă și reflex',
      'Bandas de cine, título espaciado, acercamiento lento y destello',
      'Кинорамка, титр с разрядкой, медленный наезд и блик',
    ],
    duration: 8000,
    stagger: 280,
    intro: 2000,
    fx: [
      {
        kind: 'title',
        start: 0,
        duration: 2200,
        params: { style: 'track', exit: 'fade', bg: 'dark', color: 'light' },
      },
      { kind: 'letterbox', start: 0, duration: 2800 },
      { kind: 'sweep', after: 'headline', offset: 600, duration: 1300, target: 'headline' },
    ],
    camera: [
      { preset: 'focus', start: 1900, duration: 1400, params: { intensity: 0.8 } },
      { preset: 'push-in', start: 1900, duration: 'rest', params: { intensity: 0.4 } },
    ],
  },
  {
    id: 'opener-glitch',
    category: 'opener',
    base: 'sale',
    label: [
      'Glitch opener',
      'Опенер «Глітч»',
      'Otwarcie „Glitch”',
      'Deschidere „Glitch”',
      'Apertura «Glitch»',
      'Опенер «Глитч»',
    ],
    description: [
      'A glitching title, a hard cut with speed lines and a shake',
      'Титр із глітчем, різка склейка з лініями швидкості і струсом',
      'Tytuł z glitchem, ostre cięcie z liniami prędkości',
      'Titlu cu glitch, tăietură bruscă cu linii de viteză',
      'Título con glitch, corte seco con líneas de velocidad',
      'Титр с глитчем, резкая склейка с линиями скорости и тряской',
    ],
    duration: 5000,
    stagger: 90,
    intro: 1400,
    fx: [
      {
        kind: 'title',
        start: 0,
        duration: 1400,
        params: { style: 'blur', exit: 'cut', bg: 'dark' },
      },
      { kind: 'glitch', start: 0, duration: 500 },
      { kind: 'glitch', start: 1050, duration: 700, params: { count: 14 } },
      { kind: 'flash', start: 1250, duration: 350, params: { opacity: 0.7 } },
      { kind: 'lines', start: 1350, duration: 900, params: { count: 24 } },
    ],
    camera: [
      { preset: 'roll-in', start: 1400, duration: 700, params: { intensity: 0.5 } },
      { preset: 'shake', start: 1400, duration: 500, params: { intensity: 0.8 } },
    ],
  },
  {
    id: 'opener-tech',
    category: 'opener',
    base: 'kinetic',
    label: [
      'Tech opener',
      'Опенер «Техно»',
      'Otwarcie „Tech”',
      'Deschidere „Tech”',
      'Apertura «Tech»',
      'Опенер «Техно»',
    ],
    description: [
      'Typed title, sliced reveal, a dot grid and a marker underline',
      'Надрукований титр, розкриття смугами, сітка точок і підкреслення',
      'Pisany tytuł, odsłona paskami, siatka kropek i podkreślenie',
      'Titlu tastat, dezvăluire în felii, grilă și subliniere',
      'Título tecleado, revelación en franjas, cuadrícula y subrayado',
      'Печатный титр, раскрытие полосами, сетка точек и подчёркивание',
    ],
    duration: 6000,
    stagger: 130,
    intro: 1600,
    fx: [
      { kind: 'grid', start: 0, duration: 'rest', z: 'back', backdrop: true },
      {
        kind: 'title',
        start: 0,
        duration: 1600,
        params: { style: 'type', exit: 'cut', bg: 'dark', color: 'light' },
      },
      { kind: 'slices', start: 1150, duration: 900, params: { count: 6 } },
      { kind: 'underline', after: 'headline', offset: 100, duration: 700, target: 'headline' },
    ],
    camera: [{ preset: 'zoom-burst', start: 1600, duration: 700, params: { intensity: 0.4 } }],
  },
  {
    id: 'opener-luxe',
    category: 'opener',
    base: 'elegant',
    label: [
      'Luxury opener',
      'Опенер «Люкс»',
      'Otwarcie „Luksus”',
      'Deschidere „Lux”',
      'Apertura «Lujo»',
      'Опенер «Люкс»',
    ],
    description: [
      'Title rises from a mask, a drawn frame, a slow pull-out and a sheen',
      'Титр з маски, намальована рамка, повільний відʼїзд і відблиск',
      'Tytuł z maski, rysowana ramka, powolny odjazd i połysk',
      'Titlu din mască, cadru desenat, depărtare lentă și luciu',
      'Título desde máscara, marco dibujado, alejamiento lento y brillo',
      'Титр из маски, рисованная рамка, медленный отъезд и блик',
    ],
    duration: 8000,
    stagger: 260,
    intro: 2000,
    fx: [
      {
        kind: 'title',
        start: 0,
        duration: 2100,
        params: { style: 'rise', exit: 'wipe', bg: 'dark', color: 'light' },
      },
      { kind: 'frame', start: 2000, duration: 'rest', params: { width: 3 } },
      {
        kind: 'sweep',
        after: 'headline',
        offset: 400,
        duration: 1500,
        target: 'headline',
        params: { opacity: 0.45 },
      },
    ],
    camera: [{ preset: 'pull-out', start: 1900, duration: 'rest', params: { intensity: 0.4 } }],
  },
  {
    id: 'opener-party',
    category: 'opener',
    base: 'playful',
    label: [
      'Party opener',
      'Опенер «Свято»',
      'Otwarcie „Impreza”',
      'Deschidere „Petrecere”',
      'Apertura «Fiesta»',
      'Опенер «Праздник»',
    ],
    description: [
      'A circle reveal into confetti, rings and floating shapes',
      'Розкриття колом у конфеті, кільця і фігури',
      'Odsłona kołem w konfetti, pierścienie i kształty',
      'Dezvăluire în cerc cu confetti, inele și forme',
      'Revelación circular con confeti, anillos y formas',
      'Раскрытие кругом в конфетти, кольца и летающие фигуры',
    ],
    duration: 6000,
    stagger: 140,
    intro: 600,
    fx: [
      { kind: 'shapes', start: 0, duration: 'rest', z: 'back', backdrop: true },
      { kind: 'circle', start: 0, duration: 900, params: { mode: 'reveal', count: 2 } },
      { kind: 'rings', start: 550, duration: 1300, params: { count: 3, size: 0.8 } },
      { kind: 'confetti', start: 650, duration: 2600, params: { count: 90, y: 55 } },
    ],
    camera: [{ preset: 'zoom-burst', start: 450, duration: 900, params: { intensity: 0.6 } }],
  },
  {
    id: 'opener-sale',
    category: 'opener',
    base: 'sale',
    label: [
      'Sale blast',
      'Розпродаж-вибух',
      'Wybuch wyprzedaży',
      'Explozie de reduceri',
      'Explosión de rebajas',
      'Распродажа-взрыв',
    ],
    description: [
      'Diagonal stripes, a burst and a punch on the price, a circled offer',
      'Діагональні смуги, вибух і удар на ціні, обведена пропозиція',
      'Ukośne paski, wybuch na cenie, obwiedziona oferta',
      'Dungi diagonale, explozie pe preț, ofertă încercuită',
      'Rayas diagonales, estallido en el precio, oferta en círculo',
      'Диагональные полосы, взрыв и удар на цене, обведённое предложение',
    ],
    duration: 5000,
    stagger: 100,
    intro: 550,
    fx: [
      {
        kind: 'marquee',
        start: 0,
        duration: 'rest',
        z: 'back',
        backdrop: true,
        params: { opacity: 0.12 },
      },
      { kind: 'stripes', start: 0, duration: 1000, params: { mode: 'reveal' } },
      {
        kind: 'burst',
        after: 'price',
        offset: -250,
        duration: 800,
        anchor: 'price',
        params: { count: 16, size: 0.9 },
      },
      {
        kind: 'underline',
        after: 'price',
        offset: 200,
        duration: 800,
        target: 'price',
        params: { shape: 'circle' },
      },
      {
        kind: 'underline',
        after: 'badge',
        offset: 200,
        duration: 800,
        target: 'badge',
        params: { shape: 'circle' },
        unless: 'price',
      },
    ],
    camera: [
      { preset: 'zoom-burst', start: 350, duration: 700, params: { intensity: 0.5 } },
      { preset: 'punch', after: 'price', offset: -200, duration: 450, params: { intensity: 1.3 } },
    ],
  },
  {
    id: 'opener-marquee',
    category: 'opener',
    base: 'kinetic',
    label: ['Marquee', 'Бігучий рядок', 'Pasek', 'Text derulant', 'Marquesina', 'Бегущая строка'],
    description: [
      'Giant outlined headline scrolling behind, a diagonal reveal',
      'Величезний контурний заголовок біжить на фоні, діагональне розкриття',
      'Ogromny konturowy nagłówek w tle, ukośna odsłona',
      'Titlu uriaș în contur derulând în fundal, dezvăluire diagonală',
      'Titular gigante en contorno de fondo, revelación diagonal',
      'Огромный контурный заголовок бежит на фоне, диагональное раскрытие',
    ],
    duration: 6000,
    stagger: 140,
    intro: 500,
    fx: [
      {
        kind: 'marquee',
        start: 0,
        duration: 'rest',
        z: 'back',
        backdrop: true,
        params: { opacity: 0.22 },
      },
      { kind: 'diagonal', start: 0, duration: 900, params: { mode: 'reveal' } },
    ],
    camera: [{ preset: 'drift', start: 0, duration: 'rest', params: { intensity: 0.8 } }],
  },
  {
    id: 'opener-aurora',
    category: 'opener',
    base: 'smooth',
    label: ['Aurora', 'Аврора', 'Zorza', 'Auroră', 'Aurora', 'Аврора'],
    description: [
      'Living gradient behind the design, a handheld drift and a sheen',
      'Живий градієнт за дизайном, дрейф камери і відблиск',
      'Żywy gradient za projektem, dryf kamery i połysk',
      'Gradient viu în spatele designului, derivă și luciu',
      'Degradado vivo detrás, deriva de cámara y brillo',
      'Живой градиент за дизайном, дрейф камеры и блик',
    ],
    duration: 6000,
    stagger: 170,
    intro: 0,
    fx: [
      { kind: 'aurora', start: 0, duration: 'rest', z: 'back', backdrop: true },
      { kind: 'sweep', after: 'headline', offset: 500, duration: 1400, target: 'headline' },
    ],
    camera: [
      { preset: 'focus', start: 0, duration: 1200, params: { intensity: 0.6 } },
      { preset: 'drift', start: 0, duration: 'rest' },
    ],
  },
  {
    id: 'opener-shutter',
    category: 'opener',
    base: 'product',
    label: ['Shutter reveal', 'Жалюзі', 'Żaluzje', 'Jaluzele', 'Persiana', 'Жалюзи'],
    description: [
      'Shutter blinds open onto the design, light sweep on the product',
      'Жалюзі відкривають дизайн, відблиск на товарі',
      'Żaluzje odsłaniają projekt, odblask na produkcie',
      'Jaluzelele dezvăluie designul, reflex pe produs',
      'La persiana abre el diseño, destello en el producto',
      'Жалюзи открывают дизайн, блик на товаре',
    ],
    duration: 5500,
    stagger: 150,
    intro: 600,
    fx: [
      { kind: 'shutter', start: 0, duration: 1000, params: { mode: 'reveal', count: 8 } },
      { kind: 'sweep', after: 'photo', offset: 300, duration: 1200, target: 'photo' },
    ],
    camera: [{ preset: 'push-in', start: 0, duration: 'rest', params: { intensity: 0.35 } }],
  },
  {
    id: 'opener-doors',
    category: 'opener',
    base: 'reveal',
    label: ['Doors', 'Двері', 'Drzwi', 'Uși', 'Puertas', 'Двери'],
    description: [
      'Doors open onto the design, the headline gets a marker underline',
      'Двері відчиняються в дизайн, заголовок отримує підкреслення',
      'Drzwi otwierają projekt, nagłówek dostaje podkreślenie',
      'Ușile se deschid spre design, titlul primește subliniere',
      'Las puertas abren el diseño, el título se subraya',
      'Двери открываются в дизайн, заголовок подчёркивается маркером',
    ],
    duration: 5500,
    stagger: 170,
    intro: 650,
    fx: [
      { kind: 'doors', start: 0, duration: 1100, params: { mode: 'reveal' } },
      { kind: 'underline', after: 'headline', offset: 150, duration: 800, target: 'headline' },
    ],
    camera: [{ preset: 'pull-out', start: 0, duration: 1600, params: { intensity: 0.4 } }],
  },
  {
    id: 'opener-blink',
    category: 'opener',
    base: 'kinetic',
    label: ['Don’t blink', 'Не моргай', 'Nie mrugaj', 'Nu clipi', 'No parpadees', 'Не моргай'],
    description: [
      'The text flashes word by word in hard full-screen cuts, then the design lands',
      'Текст спалахує по словах різкими склейками на весь екран, потім зʼявляється дизайн',
      'Tekst miga słowo po słowie w ostrych cięciach, potem wchodzi projekt',
      'Textul apare cuvânt cu cuvânt în tăieturi rapide, apoi designul',
      'El texto aparece palabra a palabra en cortes secos y luego el diseño',
      'Текст вспыхивает по словам резкими склейками на весь экран, потом приземляется дизайн',
    ],
    duration: 6000,
    stagger: 90,
    intro: 2200,
    fx: [
      { kind: 'captions', start: 0, duration: 2200, params: { style: 'blink' } },
      { kind: 'flash', start: 2100, duration: 300, params: { opacity: 0.8 } },
    ],
    camera: [{ preset: 'zoom-burst', start: 2200, duration: 700, params: { intensity: 0.5 } }],
  },
  {
    id: 'opener-trailer',
    category: 'opener',
    base: 'cinematic',
    label: ['Trailer', 'Трейлер', 'Zwiastun', 'Trailer', 'Tráiler', 'Трейлер'],
    description: [
      'Movie-trailer titles phrase by phrase in widescreen, then a slow push into the design',
      'Титри як у трейлері, фраза за фразою в широкому кадрі, потім повільний наїзд на дизайн',
      'Napisy jak w zwiastunie, fraza po frazie, potem powolny najazd na projekt',
      'Titluri ca în trailer, frază cu frază, apoi o apropiere lentă de design',
      'Títulos de tráiler frase a frase y luego un acercamiento lento al diseño',
      'Титры как в трейлере, фраза за фразой в широком кадре, потом медленный наезд на дизайн',
    ],
    duration: 9000,
    stagger: 260,
    intro: 3800,
    fx: [
      { kind: 'letterbox', start: 0, duration: 4400 },
      { kind: 'captions', start: 0, duration: 3800, params: { style: 'trailer', chunk: 'phrase' } },
    ],
    camera: [{ preset: 'push-in', start: 3800, duration: 'rest', params: { intensity: 0.4 } }],
  },
];
